import { LayoutGridIcon, TableIcon, type LucideIcon } from "lucide-react";

import { useAppRouteNavigation } from "@/app/app/_/routing";
import { Icon } from "@/components/atoms/Icon";
import type { ViewMode } from "@/components/organisms/DataView";
import { State } from "@/state";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const VIEW_MODE_CONFIG: Record<ViewMode, { icon: LucideIcon; label: string }> =
  {
    table: { icon: TableIcon, label: "Table view" },
    card: { icon: LayoutGridIcon, label: "Card view" },
  };

type DataViewModeToggleProps = {
  allowedViewModes?: ViewMode[];
};

export function DataViewModeToggle({
  allowedViewModes = ["table", "card"],
}: DataViewModeToggleProps) {
  const dataViewMode = State(
    (store) => store.dataViewModes[store.activeAppView] ?? "table",
  );
  const activeAppView = State((store) => store.activeAppView);
  const { navigateTo } = useAppRouteNavigation();

  // Don't render if only one mode is allowed
  if (allowedViewModes.length <= 1) {
    return null;
  }

  return (
    <ToggleGroup
      type="single"
      variant="outline"
      size="sm"
      value={dataViewMode}
      onValueChange={(mode) =>
        mode && navigateTo({ view: activeAppView, viewMode: mode as ViewMode })
      }
    >
      {allowedViewModes.map((mode) => {
        const config = VIEW_MODE_CONFIG[mode];
        return (
          <Tooltip key={mode}>
            <TooltipTrigger asChild>
              <ToggleGroupItem value={mode} aria-label={config.label}>
                <Icon ReactIcon={config.icon} />
              </ToggleGroupItem>
            </TooltipTrigger>
            <TooltipContent side="bottom">{config.label}</TooltipContent>
          </Tooltip>
        );
      })}
    </ToggleGroup>
  );
}
